import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  InputBase,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import logger from '../utils/logger';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SuperAdminIcon,
  Repeat as FollowupIcon,
  TabletMac as KioskIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { agencyApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Default app URLs (used when no custom domain is set)
const DEFAULT_APPS = {
  client: 'https://live-empower-care-client-x2bfg.kinsta.page',
  admin: 'https://live-empower-care-admin-993nv.kinsta.page',
  superadmin: 'https://live-empower-care-super-adm-tk44n.kinsta.page',
  followup: 'https://intake-form-follow-up-11ni5.kinsta.page',
  kiosk: 'https://patient-kiosk-g3lrg.kinsta.page',
};

// Get app URL for a subaccount
const getAppUrl = (subaccount, appType) => {
  const customDomains = {
    client: subaccount.client_domain,
    admin: subaccount.admin_domain,
    superadmin: subaccount.superadmin_domain,
    followup: subaccount.followup_domain,
    kiosk: subaccount.kiosk_domain,
  };

  if (customDomains[appType]) {
    return `https://${customDomains[appType]}`;
  }

  // Use default URL with tenant parameter
  return `${DEFAULT_APPS[appType]}?tenant=${subaccount.slug}`;
};

const AppLinkButton = ({ icon, label, url, color }) => (
  <Button
    size="small"
    startIcon={icon}
    endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
    onClick={() => window.open(url, '_blank')}
    sx={{
      fontSize: 11,
      px: 1,
      py: 0.5,
      color: color,
      bgcolor: `${color}10`,
      '&:hover': {
        bgcolor: `${color}20`,
      },
    }}
  >
    {label}
  </Button>
);

const SubaccountCard = ({ subaccount, onMenuOpen, onEdit }) => {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: subaccount.primary_color
                  ? `${subaccount.primary_color}15`
                  : '#06AB8B15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {subaccount.logo_url ? (
                <img
                  src={subaccount.logo_url}
                  alt={subaccount.name}
                  style={{ width: 32, height: 32, objectFit: 'contain' }}
                />
              ) : (
                <BusinessIcon
                  sx={{
                    color: subaccount.primary_color || '#06AB8B',
                    fontSize: 24,
                  }}
                />
              )}
            </Box>
            <Box>
              <Typography
                variant="h6"
                onClick={() => onEdit(subaccount.id)}
                sx={{
                  fontWeight: 600,
                  color: '#1a1a2e',
                  cursor: 'pointer',
                  '&:hover': { color: '#06AB8B', textDecoration: 'underline' },
                }}
              >
                {subaccount.name || subaccount.brand_name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {subaccount.slug}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => onMenuOpen(e, subaccount)}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* App Links */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#999', mb: 1, display: 'block' }}>
            Applications
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <AppLinkButton
              icon={<PersonIcon sx={{ fontSize: 14 }} />}
              label="Client"
              url={getAppUrl(subaccount, 'client')}
              color="#06AB8B"
            />
            <AppLinkButton
              icon={<AdminIcon sx={{ fontSize: 14 }} />}
              label="Admin"
              url={getAppUrl(subaccount, 'admin')}
              color="#3B82F6"
            />
            <AppLinkButton
              icon={<SuperAdminIcon sx={{ fontSize: 14 }} />}
              label="SuperAdmin"
              url={getAppUrl(subaccount, 'superadmin')}
              color="#8B5CF6"
            />
            <AppLinkButton
              icon={<FollowupIcon sx={{ fontSize: 14 }} />}
              label="Followup"
              url={getAppUrl(subaccount, 'followup')}
              color="#F59E0B"
            />
            <AppLinkButton
              icon={<KioskIcon sx={{ fontSize: 14 }} />}
              label="Kiosk"
              url={getAppUrl(subaccount, 'kiosk')}
              color="#E91E63"
            />
          </Box>
        </Box>

        {/* Domain Status */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LanguageIcon sx={{ fontSize: 16, color: '#999' }} />
            <Typography variant="body2" sx={{ color: '#666', fontSize: 12 }}>
              {subaccount.client_domain || subaccount.custom_domain
                ? (subaccount.client_domain || subaccount.custom_domain)
                : `Default (${subaccount.slug})`}
            </Typography>
          </Box>
          {(subaccount.client_domain || subaccount.custom_domain) && (
            <Chip
              size="small"
              label={subaccount.settings?.domain_verification?.client ? 'Verified' : 'Pending'}
              color={subaccount.settings?.domain_verification?.client ? 'success' : 'warning'}
              sx={{ fontSize: 11 }}
            />
          )}
        </Box>

        {/* Status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Chip
            size="small"
            label={subaccount.is_active ? 'Active' : 'Inactive'}
            color={subaccount.is_active ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

const Subaccounts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subaccounts, setSubaccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState(null);
  const [stats, setStats] = useState({ count: 0, max: 5 });

  // Delete Dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchSubaccounts();
  }, []);

  const fetchSubaccounts = async () => {
    try {
      const [subRes, profileRes] = await Promise.all([
        agencyApi.getSubaccounts(),
        agencyApi.getProfile(),
      ]);

      if (subRes.data.success) {
        setSubaccounts(subRes.data.subaccounts || []);
      }

      if (profileRes.data.success) {
        setStats({
          count: profileRes.data.agency.subaccountCount || 0,
          max: profileRes.data.agency.maxSubaccounts || 5,
        });
      }
    } catch (error) {
      logger.error('Error fetching subaccounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, subaccount) => {
    setMenuAnchor(event.currentTarget);
    setSelectedSubaccount(subaccount);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Edit handler - navigate to edit page
  const handleEditOpen = () => {
    handleMenuClose();
    if (selectedSubaccount) {
      navigate(`/subaccounts/${selectedSubaccount.id}/edit`);
    }
  };

  // Delete handlers
  const handleDeleteOpen = () => {
    handleMenuClose();
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedSubaccount(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubaccount) return;

    setDeleteLoading(true);

    try {
      const response = await agencyApi.deleteSubaccount(selectedSubaccount.id);

      if (response.data.success) {
        toast.success('Subaccount deleted successfully!');
        setSubaccounts((prev) => prev.filter((s) => s.id !== selectedSubaccount.id));
        setStats((prev) => ({ ...prev, count: prev.count - 1 }));
        handleDeleteClose();
      } else {
        toast.error(response.data.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredSubaccounts = subaccounts.filter(
    (sub) =>
      sub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Subaccounts
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            {stats.count} of {stats.max} subaccounts used
          </Typography>
        </Box>
        {(user?.role === 'owner' || user?.role === 'admin') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/subaccounts/create')}
            disabled={stats.count >= stats.max}
            sx={{ fontWeight: 600 }}
          >
            Create Subaccount
          </Button>
        )}
      </Box>

      {/* Search */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 3,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <SearchIcon sx={{ color: '#999' }} />
        <InputBase
          placeholder="Search subaccounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
        />
      </Paper>

      {/* Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : filteredSubaccounts.length > 0 ? (
        <Grid container spacing={3}>
          {filteredSubaccounts.map((subaccount) => (
            <Grid item xs={12} sm={6} md={4} key={subaccount.id}>
              <SubaccountCard
                subaccount={subaccount}
                onMenuOpen={handleMenuOpen}
                onEdit={(id) => navigate(`/subaccounts/${id}/edit`)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 3,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <BusinessIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
            {searchTerm ? 'No subaccounts found' : 'No subaccounts yet'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
            {searchTerm
              ? 'Try adjusting your search terms'
              : (user?.role === 'owner' || user?.role === 'admin')
                ? 'Create your first subaccount to get started'
                : 'No subaccounts have been assigned to you yet'}
          </Typography>
          {!searchTerm && (user?.role === 'owner' || user?.role === 'admin') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/subaccounts/create')}
            >
              Create Subaccount
            </Button>
          )}
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditOpen}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            window.open(getAppUrl(selectedSubaccount, 'client'), '_blank');
          }}
        >
          <OpenInNewIcon sx={{ mr: 1, fontSize: 18 }} /> Visit Site
        </MenuItem>
        {user?.role === 'owner' && (
          <MenuItem onClick={handleDeleteOpen} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Subaccount</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedSubaccount?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subaccounts;